import { makeAutoObservable, runInAction } from "mobx";
import type { SupportedLanguage } from "@aid/shared";
import { DEFAULT_LANGUAGE, normalizeLanguage } from "@aid/shared";

type TranslationModule = { default?: Record<string, unknown> } & Record<string, unknown>;

const translationLoaders: Record<SupportedLanguage, () => Promise<TranslationModule>> = {
  en: () => import("./locales/en.json"),
  ru: () => import("./locales/ru.json"),
  fr: () => import("./locales/fr.json"),
  es: () => import("./locales/es.json")
};

const loadTranslations = async (lang: SupportedLanguage): Promise<Record<string, unknown>> => {
  const data = await translationLoaders[lang]();
  return data.default || data;
};

function applyDocumentLanguage(language: SupportedLanguage): void {
  if (typeof document !== "undefined") document.documentElement.lang = language;
}

class I18nStore {
  language: SupportedLanguage = DEFAULT_LANGUAGE;
  translations: Record<string, unknown> = {};
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  async init() {
    if (typeof window === "undefined") {
      await this.setLanguage(DEFAULT_LANGUAGE);
      return;
    }
    await this.setLanguage(normalizeLanguage(localStorage.getItem("aid_language")));
  }

  async setLanguage(lang: SupportedLanguage) {
    if (this.language === lang && Object.keys(this.translations).length > 0) {
      return;
    }
    this.loading = true;
    try {
      const translations = await loadTranslations(lang);
      runInAction(() => {
        this.language = lang;
        this.translations = translations;
        this.loading = false;
        applyDocumentLanguage(lang);
      });
      if (typeof window === "undefined") {
        return;
      }
      localStorage.setItem("aid_language", lang);

      const token = localStorage.getItem("rpg_token");
      if (token) {
        fetch("/api/auth/language", {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ language: lang })
        }).then(async res => {
          if (res.ok) {
            const { authStore } = await import("../stores/authStore.js");
            if (authStore.user) {
              runInAction(() => {
                authStore.user!.language = lang;
              });
            }
          }
        }).catch(err => {
          console.error("Failed to update language on server:", err);
        });
      }
    } catch (err) {
      console.error("Failed to load language pack:", err);
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  t = (key: string, params?: Record<string, string | number>): string => {
    const parts = key.split(".");
    let val: unknown = this.translations;
    for (const part of parts) {
      if (val && typeof val === "object" && part in (val as Record<string, unknown>)) {
        val = (val as Record<string, unknown>)[part];
      } else {
        return key;
      }
    }
    if (typeof val !== "string") {
      return key;
    }
    if (params) {
      let formatted = val;
      for (const [k, v] of Object.entries(params)) {
        formatted = formatted.replace(new RegExp(`{${k}}`, "g"), String(v));
      }
      return formatted;
    }
    return val;
  };
}

export const i18nStore = new I18nStore();
// Initialize the i18n store immediately so translations are available on boot
i18nStore.init().catch(err => {
  console.error("Failed to initialize i18n store on boot:", err);
});
